import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { LibraryNode, LibraryNodeType } from "../../lib/types";

/**
 * Direct child nodes of `parentId` filtered to a single type. Unlike
 * `useListChildren` (lightweight `list_children`), this calls `list_tree`
 * which returns full nodes — including `document` and `memory_type` — so the
 * Memories panel can render the body inline without a second fetch per row.
 *
 * Polls at 30s (Sprint 5n cadence) so content created via MCP/Claude shows up
 * in an open cockpit without a manual refresh.
 *
 * Sprint 14g — pass `includeArchived` to surface archived nodes (drives the
 * "Show archived" chip in the Pages / Memories panels).
 */
export function useChildNodes(parentId: string | undefined, type: LibraryNodeType, includeArchived?: boolean) {
  return useQuery<LibraryNode[]>({
    queryKey: [...(parentId ? queryKeys.childNodes(parentId, type) : ["child-nodes", "", type]), includeArchived ?? false],
    queryFn: () => {
      if (!parentId) return [];
      return mcpClient.listTree({ parent_id: parentId, type, depth: 1, include_archived: includeArchived });
    },
    enabled: !!parentId,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}
